"use client";

import * as React from "react";
import { Download, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";
import { BottomSheet } from "./bottom-sheet";
import { cn } from "../../lib/utils";

const formatCurrency = (amount: number, currency = "USD") => {
  const safeAmount = amount ?? 0;
  const locale = currency === "INR" ? "en-IN" : "en-US";
  const symbol = currency === "INR" ? "₹ " : "$";
  return `${symbol}${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeAmount)}`;
};

export interface PayoutHistoryItem {
  id: string;
  date: string;
  month: string;
  amount: string;
  rawAmount: number;
  currency: string;
  payoutMethod: string;
  referenceId?: string;
  country?: string;
  clicks: number;
  applications: number;
  referralBonus?: string;
  receiptUrl?: string;
  grossAmount?: number;
  platformFee?: number;
  tds?: number;
  platformFeeRate?: number;
  tdsRate?: number;
}

export interface PayoutHistoryBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  payouts: PayoutHistoryItem[];
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
}

export function PayoutHistoryBottomSheet({
  isOpen,
  onClose,
  payouts,
  title = "Payouts",
  subtitle = "View the details of payouts received for Ad network.",
  isLoading = false,
}: PayoutHistoryBottomSheetProps) {
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      className="bg-[#F9FAFB] px-0 pb-6 pt-2 h-[70vh] max-h-[90vh]"
      showDragHandle
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-4 pb-4 space-y-1">
          <p className="font-plus-jakarta-sans text-lg font-bold text-[#101828]">{title}</p>
          <p className="text-sm text-[#667085]">{subtitle}</p>
        </div>

        {/* Content */}
        <div className={cn(
          "flex-1 px-4 space-y-4",
          payouts.length > 0 ? "overflow-y-auto" : "overflow-hidden"
        )}>
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center space-y-2">
              <p className="text-sm font-medium text-[#101828]">No payouts yet</p>
              <p className="text-xs text-[#667085]">When you receive a payout, it will appear here.</p>
            </div>
          ) : (
            payouts.map((payout) => (
              <PayoutCard key={payout.id} payout={payout} />
            ))
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

function PayoutCard({ payout }: { payout: PayoutHistoryItem }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (payout.receiptUrl) {
      window.open(payout.receiptUrl, "_blank");
    }
  };

  const handleCopy = async (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const isIndia = payout.country === "IN";
  const platformFeeRate = payout.platformFeeRate !== undefined ? payout.platformFeeRate : 0.30;
  const tdsRate = payout.tdsRate !== undefined ? payout.tdsRate : (isIndia ? 0.02 : 0.00);

  const grossAmountVal = payout.grossAmount !== undefined ? payout.grossAmount : payout.rawAmount;
  const platformFeeVal = payout.platformFee !== undefined ? payout.platformFee : (grossAmountVal * platformFeeRate);
  const tdsVal = payout.tds !== undefined ? payout.tds : (tdsRate > 0 ? ((grossAmountVal - platformFeeVal) * tdsRate) : 0);

  const currency = payout.currency || "USD";
  const grossFormatted = formatCurrency(grossAmountVal, currency);
  const platformFeeFormatted = formatCurrency(platformFeeVal, currency);
  const tdsFormatted = tdsRate > 0 ? formatCurrency(tdsVal, currency) : "";
  const netAmountVal = payout.grossAmount !== undefined ? payout.rawAmount : (grossAmountVal - platformFeeVal - tdsVal);
  const netFormatted = formatCurrency(netAmountVal, currency);

  const refLabel = payout.payoutMethod === "bank" ? "UTR" : "Transaction ID";

  return (
    <div
      className="flex flex-col gap-3 rounded-2xl border border-[#F2F4F7] bg-white p-4 shadow-sm cursor-pointer hover:border-black/10 transition-all select-none"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Top Row: Date and Chevron/Download */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="text-xs font-medium text-[#98A2B3]">
            Paid on {payout.date}
          </p>
          {!isExpanded && (
            <p className="font-plus-jakarta-sans text-lg font-bold text-[#101828]">
              Net {netFormatted}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {payout.receiptUrl && (
            <button
              onClick={handleDownload}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-[#F2F4F7] text-[#667085] transition-colors"
              aria-label="Download receipt"
            >
              <Download className="size-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="size-5 text-[#667085]" />
          ) : (
            <ChevronDown className="size-5 text-[#667085]" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-2 space-y-4 border-t border-[#F2F4F7] pt-4 animate-in fade-in slide-in-from-top-1 duration-200">
          <div>
            <p className="font-plus-jakarta-sans text-base font-bold text-[#344054]">
              {payout.month}
            </p>
          </div>

          {/* Breakdown list */}
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-[#667085]">Gross earnings</span>
              <span className="font-medium text-[#344054]">{grossFormatted}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#667085]">Platform fee ({Math.round(platformFeeRate * 100)}%)</span>
              <span className="font-medium text-[#D92D20]">- {platformFeeFormatted}</span>
            </div>
            {tdsRate > 0 && (
              <div className="flex justify-between">
                <span className="text-[#667085]">TDS ({Math.round(tdsRate * 100)}%)</span>
                <span className="font-medium text-[#D92D20]">- {tdsFormatted}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-[#F2F4F7] pt-2 text-sm font-bold">
              <span className="text-[#101828]">Net Amount Paid</span>
              <span className="text-[#101828]">{netFormatted}</span>
            </div>
          </div>

          {/* UTR / Transaction ID */}
          {payout.referenceId && (
            <div className="flex flex-col gap-1 rounded-xl bg-[#F9FAFB] p-3 border border-[#F2F4F7]">
              <p className="text-[10px] font-semibold text-[#667085] uppercase tracking-wider">
                {refLabel}
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-mono font-medium text-[#344054] truncate select-all">
                  {payout.referenceId}
                </p>
                <button
                  type="button"
                  onClick={(e) => handleCopy(e, payout.referenceId!)}
                  className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-[#E4E7EC] text-[#667085] transition-colors"
                  aria-label={`Copy ${refLabel}`}
                >
                  {copied ? (
                    <Check className="size-3.5 text-[#2EB67D]" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </button>
              </div>
            </div>
          )}

          {payout.referralBonus && (
            <p className="text-xs font-bold text-[#ED5AA9]">
              {payout.referralBonus} Referral bonus
            </p>
          )}
        </div>
      )}
    </div>
  );
}
